// shell/components/table/columns/text-column.ts

import { TextColumnType } from '../types';
import { ColumnRenderer, CellRenderContext, CellUpdateType, CellRenderResult } from './column-base';

export class TextColumnRenderer<T = unknown> extends ColumnRenderer<T> {
    readonly supportsGrid = true;
    readonly supportsTable = true;

    constructor(protected config: TextColumnType<T>) {
        super();
    }

    getGridCellClass(): string {
        return 'cs-column-text';
    }

    getTableCellClass(): string {
        return 'cs-column-text';
    }

    buildGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement): HTMLElement {
        const span = this.buildTextElement(ctx);
        this.cacheElement(cellWrapper, 'content', span);

        if (this.shouldShadeInGrid(ctx)) {
            this.applyShade(cellWrapper, ctx.shadeRank, ctx.heatMapBounds);
        }
        return span;
    }

    buildTableCell(ctx: CellRenderContext<T>, cell: HTMLElement): HTMLElement {
        const span = this.buildTextElement(ctx);
        this.cacheElement(cell, 'content', span);

        if (this.shouldShadeInTable(ctx)) {
            this.applyShade(cell, ctx.shadeRank, ctx.heatMapBounds);
        }
        return span;
    }

    protected buildTextElement(ctx: CellRenderContext<T>): HTMLSpanElement {
        const span = document.createElement('span');
        span.className = 'cs-column-text__content';

        // Apply text color
        if (this.config.textColor) {
            try {
                const color = this.config.textColor(ctx.value, ctx.item);
                if (color) span.style.color = color;
            } catch (e) {
                console.error('Text color callback error:', e);
            }
        }

        // Set content
        let treatAsHtml = false;
        try {
            treatAsHtml = this.config.treatAsHtml?.(ctx.value, ctx.item) ?? false;
        } catch (e) {
            console.error('treatAsHtml callback error:', e);
        }

        const text = this.formatValue(ctx.value, this.config.converter, ctx.item);

        if (treatAsHtml) {
            span.innerHTML = text;
        } else {
            span.textContent = text;
        }

        return span;
    }

    protected shouldShadeInGrid(ctx: CellRenderContext<T>): boolean {
        return ctx.column.grid?.heatMap !== false && ctx.column.heatMap?.enabled === true;
    }

    protected shouldShadeInTable(ctx: CellRenderContext<T>): boolean {
        return ctx.column.table?.heatMap !== false && ctx.column.heatMap?.enabled === true;
    }

    updateGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        if (type === CellUpdateType.Shades) {
            if (this.shouldShadeInGrid(ctx)) {
                this.applyShade(cellWrapper, ctx.shadeRank, ctx.heatMapBounds);
            }
            return { contentElement: contentEl };
        }

        // Full update - but check if content actually changed first
        const text = this.formatValue(ctx.value, this.config.converter, ctx.item);
        const treatAsHtml = this.config.treatAsHtml?.(ctx.value, ctx.item) ?? false;

        // Skip DOM update if content is identical
        if (contentEl) {
            const existingContent = treatAsHtml ? contentEl.innerHTML : contentEl.textContent;
            if (existingContent === text) {
                // Content unchanged - just update shading if needed
                if (this.shouldShadeInGrid(ctx)) {
                    this.applyShade(cellWrapper, ctx.shadeRank, ctx.heatMapBounds);
                }
                return { contentElement: contentEl };
            }
        }

        // Content changed - rebuild
        const newSpan = this.buildTextElement(ctx);
        this.cacheElement(cellWrapper, 'content', newSpan);

        if (contentEl) {
            contentEl.replaceWith(newSpan);
        } else {
            cellWrapper.appendChild(newSpan);
        }

        if (this.shouldShadeInGrid(ctx)) {
            this.applyShade(cellWrapper, ctx.shadeRank, ctx.heatMapBounds);
        }

        return { contentElement: newSpan };
    }

    updateTableCell(ctx: CellRenderContext<T>, cell: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        if (type === CellUpdateType.Shades) {
            if (this.shouldShadeInTable(ctx)) {
                this.applyShade(cell, ctx.shadeRank, ctx.heatMapBounds);
            }
            return { contentElement: contentEl };
        }

        // Full update - but check if content actually changed first
        const text = this.formatValue(ctx.value, this.config.converter, ctx.item);
        const treatAsHtml = this.config.treatAsHtml?.(ctx.value, ctx.item) ?? false;

        // Skip DOM update if content is identical
        if (contentEl) {
            const existingContent = treatAsHtml ? contentEl.innerHTML : contentEl.textContent;
            if (existingContent === text) {
                // Content unchanged - just update shading if needed
                if (this.shouldShadeInTable(ctx)) {
                    this.applyShade(cell, ctx.shadeRank, ctx.heatMapBounds);
                }
                return { contentElement: contentEl };
            }
        }

        // Content changed - rebuild
        const newSpan = this.buildTextElement(ctx);
        this.cacheElement(cell, 'content', newSpan);

        if (contentEl) {
            contentEl.replaceWith(newSpan);
        } else {
            cell.appendChild(newSpan);
        }

        if (this.shouldShadeInTable(ctx)) {
            this.applyShade(cell, ctx.shadeRank, ctx.heatMapBounds);
        }

        return { contentElement: newSpan };
    }
}
